/* SST > Levantamiento de Condiciones Inseguras: acciones correctivas asignadas.
 *
 * El levantamiento se carga en EMSYS; acá el asignado ve sus pendientes, el
 * detalle y carga las acciones ejecutadas (con adjunto opcional). api-central
 * reenvía todo a EMSYS, que filtra por la cédula del usuario logueado.
 */
import api from "src/services/api";

const BASE = "sst/acciones-correctivas";

export type ColorSemaforo = "verde" | "amarillo" | "rojo" | "gris";

export interface Semaforo {
  color: ColorSemaforo;
  dias_restantes: number | null;
  texto: string;
}

export type EstadoLevantamiento =
  | "PENDIENTE_ACCION"
  | "DEVUELTO"
  | "PENDIENTE_CIERRE"
  | "CERRADO"
  | "ANULADO";

export interface LevantamientoResumen {
  id: number;
  estado: EstadoLevantamiento;
  estado_label: string;
  condicion: "LUGAR" | "EPI";
  condicion_label: string;
  ps_codigo?: string | null;
  ps_nombre?: string | null;
  colaborador_legajo?: string | null;
  colaborador_nombre?: string | null;
  unidad_negocio: string;
  area: string;
  anomalia: string;
  asignado_legajo: string;
  asignado_nombre: string;
  fecha_inspeccion: string;
  fecha_asignacion: string;
  fecha_limite: string;
  sla_dias: number;
  devoluciones: number;
  creado_por_nombre?: string | null;
  semaforo: Semaforo;
}

export interface AdjuntoLevantamiento {
  id: number;
  tipo: "INFORME" | "ACCION";
  ronda: number;
  nombre_archivo: string;
  mime_type: string;
  subido_en: string;
  /** URL firmada de Cloudinary; vence en minutos. */
  url_firmada?: string;
}

export interface HistorialLevantamiento {
  fecha: string;
  accion: string;
  estado_nuevo: string;
  usuario?: string | null;
  origen: string;
  detalle?: string | null;
}

export interface LevantamientoDetalle extends LevantamientoResumen {
  sector?: string | null;
  fiscal_nombre?: string | null;
  colaborador_cedula?: string | null;
  colaborador_telefono?: string | null;
  colaborador_fecha_ingreso?: string | null;
  descripcion: string;
  acciones_correctivas?: string | null;
  acciones_en?: string | null;
  aprobado?: boolean | null;
  conforme?: boolean | null;
  comentario_cierre?: string | null;
  cerrado_en?: string | null;
  adjuntos: AdjuntoLevantamiento[];
  historial: HistorialLevantamiento[];
  puede_responder: boolean;
}

interface ApiOk<T> {
  ok: true;
  data: T;
}

const SstAPI = {
  /** pendientes=true: solo las que esperan acciones del usuario (card del inicio). */
  listar: (pendientes = false) =>
    api.get<ApiOk<LevantamientoResumen[]>>(BASE, { params: pendientes ? { pendientes: 1 } : {} }),
  detalle: (id: number) => api.get<ApiOk<LevantamientoDetalle>>(`${BASE}/${id}`),
  responder: (id: number, acciones: string, archivo?: File | null) => {
    const form = new FormData();
    form.append("acciones", acciones);
    if (archivo) form.append("archivo", archivo);
    return api.post<ApiOk<{ info: string }>>(`${BASE}/${id}/responder`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const COLORES_SEMAFORO: Record<ColorSemaforo, { texto: string; fondo: string }> = {
  verde: { texto: "#1e7e34", fondo: "#e3f4e7" },
  amarillo: { texto: "#9a7300", fondo: "#fff4cc" },
  rojo: { texto: "#b21f2d", fondo: "#fbe3e5" },
  gris: { texto: "#6c757d", fondo: "#eceeef" },
};

export default SstAPI;

// ---------------------------------------------------------------------------
// Solicitudes a SST (Solicitudes > SST). Los tipos y sus campos los declara
// EMSYS (catalogo); el formulario se arma con eso.
// ---------------------------------------------------------------------------

const BASE_SOL = "sst/solicitudes";

export type TipoCampoSolicitud = "PS" | "FECHA" | "HORA" | "TEXTO" | "TEXTAREA" | "OPCION" | "COLABORADORES";

export interface CampoSolicitud {
  codigo: string;
  etiqueta: string;
  tipo: TipoCampoSolicitud;
  obligatorio: boolean;
  max?: number | null;
  opciones: { valor: string; etiqueta: string }[];
}

export interface TipoSolicitudSst {
  codigo: string;
  etiqueta: string;
  /** "pasada": la fecha no puede ser futura (incidente); "futura": no puede ser anterior a hoy. */
  regla_fecha: "pasada" | "futura";
  campos: CampoSolicitud[];
}

export interface PsBusqueda {
  id: number;
  codigo: string;
  nombre: string;
}

export interface ColaboradorBusqueda {
  legajo: string;
  nombre: string;
  cedula?: string;
  cargo?: string;
}

export type EstadoSolicitudSst = "PENDIENTE" | "DEVUELTA" | "APROBADA" | "RECHAZADA" | "CANCELADA";

export interface SolicitudSstResumen {
  id: number;
  tipo: string;
  tipo_label: string;
  estado: EstadoSolicitudSst;
  estado_label: string;
  ps_nombre?: string | null;
  fecha?: string | null;
  hora?: string | null;
  enviado_en?: string | null;
  gestionado_en?: string | null;
  devoluciones: number;
}

export interface SolicitudSstDetalle extends SolicitudSstResumen {
  campos: { etiqueta: string; valor: string | null; bloque: boolean }[];
  valores: Record<string, any>;
  comentario_sst?: string | null;
  gestionado_por?: string | null;
  motivo_cancelacion?: string | null;
  historial: HistorialLevantamiento[];
  puede_corregir: boolean;
  puede_cancelar: boolean;
}

export const SolicitudSstAPI = {
  permiso: () => api.get<ApiOk<{ puede_solicitar: boolean; mensaje?: string | null }>>(`${BASE_SOL}/permiso`),
  catalogo: () => api.get<ApiOk<{ tipos: TipoSolicitudSst[] }>>(`${BASE_SOL}/catalogo`),
  puntosServicio: (q: string) => api.get<ApiOk<PsBusqueda[]>>(`${BASE_SOL}/puntos-servicio`, { params: { q } }),
  colaboradores: (q: string) => api.get<ApiOk<ColaboradorBusqueda[]>>(`${BASE_SOL}/colaboradores`, { params: { q } }),
  mis: () => api.get<ApiOk<SolicitudSstResumen[]>>(BASE_SOL),
  crear: (payload: Record<string, any>) => api.post<ApiOk<{ id: number; info: string }>>(BASE_SOL, payload),
  detalle: (id: number) => api.get<ApiOk<SolicitudSstDetalle>>(`${BASE_SOL}/${id}`),
  reenviar: (id: number, payload: Record<string, any>) =>
    api.post<ApiOk<{ info: string }>>(`${BASE_SOL}/${id}/reenviar`, payload),
  cancelar: (id: number, motivo: string) =>
    api.post<ApiOk<{ info: string }>>(`${BASE_SOL}/${id}/cancelar`, { motivo }),
};

/* Quien puede solicitar a SST lo decide EMSYS (fiscales, jefes de operaciones y
 * todos los administrativos, por el tipo de funcionario del DWH, que la app no
 * conoce). Se consulta una vez por sesion: el Menu se remonta en cada cambio de
 * ruta. "1" / "0"; sin valor = todavia no se sabe. Se limpia al cerrar sesion. */
export const SST_PERMISO_SESSION_KEY = "sst_puede_solicitar_session";

export const permisoSstEnSesion = (): boolean | null => {
  const v = sessionStorage.getItem(SST_PERMISO_SESSION_KEY);
  return v === null ? null : v === "1";
};

// Menu y tarjetas del inicio lo piden a la vez al entrar: comparten la misma request.
let permisoEnCurso: Promise<boolean | null> | null = null;

export const cargarPermisoSst = (): Promise<boolean | null> => {
  const guardado = permisoSstEnSesion();
  if (guardado !== null) return Promise.resolve(guardado);
  if (!permisoEnCurso) {
    permisoEnCurso = SolicitudSstAPI.permiso()
      .then((r) => {
        const puede = Boolean(r.data.data?.puede_solicitar);
        sessionStorage.setItem(SST_PERMISO_SESSION_KEY, puede ? "1" : "0");
        return puede;
      })
      .catch(() => null) // sin marcar: se reintenta en la proxima navegacion
      .finally(() => {
        permisoEnCurso = null;
      });
  }
  return permisoEnCurso;
};

export const ESTADOS_SOLICITUD_SST: Record<string, { etiqueta: string; color: string }> = {
  PENDIENTE: { etiqueta: "Pendiente de SST", color: "#e67e22" },
  DEVUELTA: { etiqueta: "Devuelta", color: "#8e44ad" },
  APROBADA: { etiqueta: "Aprobada", color: "#27ae60" },
  RECHAZADA: { etiqueta: "Rechazada", color: "#c0392b" },
  CANCELADA: { etiqueta: "Cancelada", color: "#7f8c8d" },
};
